import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useTenant } from '../../lib/tenant-context';
import { useLoyaltyDashboard, useLoyaltyToggle } from '../../lib/loyalty-hooks';
import { Card } from '../../components/ui/Card';
import { Users, Star, Gift, Trophy, Power, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
export function LoyaltyDashboardPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const {
    customers,
    loading
  } = useLoyaltyDashboard(restaurant.restaurant_id);
  const {
    isActive,
    loading: toggleLoading,
    saving,
    toggle
  } = useLoyaltyToggle(restaurant.restaurant_id);
  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + (c.current_points || 0), 0);
  const totalRewardsAvailable = customers.reduce((sum, c) => sum + (c.available_rewards_count || 0), 0);
  const totalRewardsRedeemed = customers.reduce((sum, c) => sum + (c.redeemed_rewards_count || 0), 0);
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-3571">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3572">
        <div data-id="element-3573">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3574">
            Programa de Lealtad
          </h1>
          <p className="text-gray-500" data-id="element-3575">
            Gestiona tus clientes frecuentes y beneficios
          </p>
        </div>

        {/* Activation toggle */}
        <Card className={`p-6 border-2 transition-colors ${isActive ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50'}`} data-id="element-3576">
          <div className="flex items-center justify-between gap-6 flex-wrap" data-id="element-3577">
            <div className="flex items-center gap-4" data-id="element-3578">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-200'}`} data-id="element-3579">
                <Power className={`w-6 h-6 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-3580" />
              </div>
              <div data-id="element-3581">
                <div className="flex items-center gap-2" data-id="element-3582">
                  <h2 className="font-bold text-gray-900" data-id="element-3583">
                    Sistema de Lealtad
                  </h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-3584">
                    {toggleLoading ? '...' : isActive ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-3585">
                  {isActive ? 'Cada pedido pagado con teléfono acumula puntos para el cliente.' : 'Los pedidos no acumulan puntos. Activa el programa para empezar a premiar a tus clientes.'}
                </p>
              </div>
            </div>

            <button onClick={() => toggle(!isActive)} disabled={saving || toggleLoading} className={`relative inline-flex items-center h-9 px-1 rounded-full transition-colors w-20 shrink-0 disabled:opacity-60 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-label={isActive ? 'Desactivar lealtad' : 'Activar lealtad'} data-id="element-3586">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow transform transition-transform ${isActive ? 'translate-x-11' : 'translate-x-0'}`} data-id="element-3587">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" data-id="element-3588" />}
              </span>
            </button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3589">
          <Card className="p-6" data-id="element-3590">
            <div className="flex items-center gap-4" data-id="element-3591">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3592">
                <Users className="w-6 h-6 text-blue-600" data-id="element-3593" />
              </div>
              <div data-id="element-3594">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3595">
                  Clientes Leales
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3596">
                  {totalCustomers}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3597">
            <div className="flex items-center gap-4" data-id="element-3598">
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3599">
                <Star className="w-6 h-6 text-yellow-600" data-id="element-3600" />
              </div>
              <div data-id="element-3601">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3602">
                  Puntos Emitidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3603">
                  {totalPoints}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3604">
            <div className="flex items-center gap-4" data-id="element-3605">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3606">
                <Gift className="w-6 h-6 text-green-600" data-id="element-3607" />
              </div>
              <div data-id="element-3608">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3609">
                  Beneficios Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3610">
                  {totalRewardsAvailable}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3611">
            <div className="flex items-center gap-4" data-id="element-3612">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3613">
                <Trophy className="w-6 h-6 text-purple-600" data-id="element-3614" />
              </div>
              <div data-id="element-3615">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3616">
                  Beneficios Redimidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3617">
                  {totalRewardsRedeemed}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="overflow-hidden" data-id="element-3618">
          <div className="p-6 border-b border-gray-200" data-id="element-3619">
            <h2 className="font-bold text-gray-900" data-id="element-3620">Directorio de Clientes</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-3621">
            <table className="w-full text-left text-sm" data-id="element-3622">
              <thead className="bg-gray-50 text-gray-500" data-id="element-3623">
                <tr data-id="element-3624">
                  <th className="px-6 py-4 font-medium" data-id="element-3625">Cliente</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3626">Teléfono</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3627">Visitas</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3628">
                    Total Gastado
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3629">
                    Ticket Promedio
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3630">Puntos</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3631">Nivel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-3632">
                {loading ? <tr data-id="element-3633">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3634">
                      Cargando clientes...
                    </td>
                  </tr> : customers.length === 0 ? <tr data-id="element-3635">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3636">
                      No hay clientes registrados aún.
                    </td>
                  </tr> : customers.map(customer => <tr key={customer.customer_id} className="hover:bg-gray-50" data-id="element-3637">
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3638">
                        {customer.name || 'Sin nombre'}
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-3639">
                        {customer.phone}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3640">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3641">
                        {formatCurrency(customer.total_spent, restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500" data-id="element-3642">
                        {formatCurrency(customer.total_spent / Math.max(1, customer.total_orders), restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-brand-charcoal" data-id="element-3643">
                        {customer.current_points}
                      </td>
                      <td className="px-6 py-4" data-id="element-3644">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3645">
                          {customer.current_level || 'Inicial'}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>;
}